import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';

/**
 * Text date-of-birth field (no native date/calendar picker).
 * US: MM/DD/YYYY. Everywhere else: DD/MM/YYYY.
 * Stored payload stays { day, month, year, string: DD/MM/YYYY }.
 */

const formatDobDigits = (raw) => {
  let value = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (value.length > 2) value = `${value.slice(0, 2)}/${value.slice(2)}`;
  if (value.length > 5) value = `${value.slice(0, 5)}/${value.slice(5)}`;
  return value;
};

const parseDobText = (text, isUSFormat) => {
  const match = String(text || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);
  const day = isUSFormat ? second : first;
  const month = isUSFormat ? first : second;
  const birthDate = new Date(year, month - 1, day);
  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return null;
  }
  return {
    day,
    month,
    year,
    string: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`,
  };
};

const SmartDateOfBirth = ({ country = 'GB', value = null, onChange, className = '' }) => {
  const isUSFormat = String(country || '').toUpperCase() === 'US';
  const [text, setText] = useState(() => {
    if (!value?.day || !value?.month || !value?.year) return '';
    const d = String(value.day).padStart(2, '0');
    const m = String(value.month).padStart(2, '0');
    const y = String(value.year);
    return isUSFormat ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
  });

  useEffect(() => {
    onChange?.(text ? parseDobText(text, isUSFormat) : null);
    // Re-interpret the same digits when country format changes (US vs DD/MM).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUSFormat]);

  return (
    <Input
      id="dateOfBirth"
      name="dateOfBirth"
      type="text"
      inputMode="numeric"
      autoComplete="bday"
      maxLength={10}
      required
      value={text}
      placeholder={isUSFormat ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
      onChange={(e) => {
        const next = formatDobDigits(e.target.value);
        setText(next);
        onChange?.(next.length === 10 ? parseDobText(next, isUSFormat) : null);
      }}
      className={`bg-gray-900 border-gray-700 text-white ${className}`}
    />
  );
};

export default SmartDateOfBirth;
